import { env } from "constants/envConfig";

export type LinearLabel = {
  id: string;
  name: string;
  color?: string;
};

export type LinearIssueDetail = {
  id: string;
  identifier: string;
  title: string;
  url?: string;
  priority?: number;
  priorityLabel?: string;
  updatedAt?: string;
  createdAt?: string;
  description?: string;
  project?: { id: string };
  state: { id: string; name: string; type?: string; color?: string };
  assignee?: { id: string; name: string; displayName?: string };
  creator?: { id: string; name: string };
  botActor?: { userDisplayName: string; name: string };
  labels: LinearLabel[];
  comments: Array<{
    id: string;
    body: string;
    createdAt: string;
    user?: { id: string; name: string };
    botActor?: { userDisplayName: string; name: string };
  }>;
};

export async function getShareIssue(
  token: string,
): Promise<LinearIssueDetail> {
  const base = (env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const url = `${base}/share/issue/${encodeURIComponent(token)}`;
  const res = await fetch(url);
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok) {
    const raw = await res.text();
    let message = "Link invalid or expired.";
    if (contentType.includes("application/json")) {
      try {
        const json = JSON.parse(raw);
        message =
          json?.error?.displayMessage ?? json?.error?.message ?? message;
      } catch {
        /* ignore */
      }
    }
    throw new Error(message);
  }
  const data = await res.json();
  return data?.data?.issue ?? data?.issue;
}

export async function createShareComment(
  token: string,
  body: string,
): Promise<void> {
  const base = (env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const url = `${base}/share/issue/${encodeURIComponent(token)}/comments`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) {
    throw new Error("Failed to post comment");
  }
}

export function getShareImageProxyUrl(token: string, imageUrl: string): string {
  const base = (env.REACT_APP_API_URL || "").replace(/\/$/, "");
  return `${base}/share/issue/${encodeURIComponent(token)}/image?url=${encodeURIComponent(imageUrl)}`;
}

/**
 * Replaces markdown image URLs in text with share proxy URLs so images load without auth.
 */
export function replaceImageUrlsInMarkdown(text: string, token: string): string {
  return text.replace(
    /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g,
    (_, alt, url) => `![${alt}](${getShareImageProxyUrl(token, url)})`,
  );
}
